// ICT Engine — detects core concepts from OHLC arrays.
// All detectors are pure: given bars (and optional correlated bars), they return arrays
// of structured events keyed by bar index so the UI can overlay + sound-alert on them.

// Swing High/Low using fractal (left/right lookback)
export function detectSwings(bars, lookback = 2) {
  const highs = [];
  const lows = [];
  for (let i = lookback; i < bars.length - lookback; i++) {
    let isHigh = true, isLow = true;
    for (let k = 1; k <= lookback; k++) {
      if (bars[i].high <= bars[i - k].high || bars[i].high <= bars[i + k].high) isHigh = false;
      if (bars[i].low >= bars[i - k].low || bars[i].low >= bars[i + k].low) isLow = false;
    }
    if (isHigh) highs.push({ index: i, price: bars[i].high, time: bars[i].time });
    if (isLow)  lows.push({  index: i, price: bars[i].low,  time: bars[i].time });
  }
  return { swingHighs: highs, swingLows: lows };
}

// Fair Value Gap: 3-bar imbalance. Bullish: bar[i-2].high < bar[i].low. Bearish: bar[i-2].low > bar[i].high
export function detectFVGs(bars) {
  const fvgs = [];
  for (let i = 2; i < bars.length; i++) {
    const a = bars[i - 2], c = bars[i];
    if (a.high < c.low) fvgs.push({ index: i, type: "bullish", top: c.low, bottom: a.high, time: bars[i - 1].time, mitigated: false });
    if (a.low  > c.high) fvgs.push({ index: i, type: "bearish", top: a.low, bottom: c.high, time: bars[i - 1].time, mitigated: false });
  }
  // Mark mitigated FVGs (Inverse FVG = mitigated then reversed — same record flagged)
  for (const f of fvgs) {
    for (let j = f.index + 1; j < bars.length; j++) {
      if (f.type === "bullish" && bars[j].low <= f.bottom) { f.mitigated = true; f.mitigatedIndex = j; break; }
      if (f.type === "bearish" && bars[j].high >= f.top)   { f.mitigated = true; f.mitigatedIndex = j; break; }
    }
  }
  return fvgs;
}

// Inverse FVG: an FVG that was traded through, flipping polarity
export function detectIFVGs(fvgs) {
  return fvgs.filter(f => f.mitigated).map(f => ({
    ...f, ifvg: true, polarity: f.type === "bullish" ? "bearish" : "bullish",
  }));
}

// BOS / MSS
// BOS = breach of last swing in the direction of prevailing structure (continuation)
// MSS = breach of last swing AGAINST prevailing structure (reversal)
export function detectStructureShifts(bars, swings) {
  const events = [];
  const allSwings = [
    ...swings.swingHighs.map(s => ({ ...s, kind: "high" })),
    ...swings.swingLows.map(s  => ({ ...s, kind: "low"  })),
  ].sort((a, b) => a.index - b.index);

  let trend = "neutral";
  for (let i = 1; i < allSwings.length; i++) {
    const s = allSwings[i];
    for (let j = s.index + 1; j < bars.length; j++) {
      const bar = bars[j];
      if (s.kind === "high" && bar.close > s.price) {
        const isBOS = trend === "bullish";
        const isMSS = trend === "bearish";
        if (isBOS || isMSS) {
          events.push({ index: j, type: isMSS ? "MSS" : "BOS", direction: "bullish", refPrice: s.price, time: bar.time });
        }
        trend = "bullish";
        break;
      }
      if (s.kind === "low" && bar.close < s.price) {
        const isBOS = trend === "bearish";
        const isMSS = trend === "bullish";
        if (isBOS || isMSS) {
          events.push({ index: j, type: isMSS ? "MSS" : "BOS", direction: "bearish", refPrice: s.price, time: bar.time });
        }
        trend = "bearish";
        break;
      }
    }
  }
  // dedupe by (index,type,direction)
  const seen = new Set();
  return events.filter(e => {
    const k = `${e.index}-${e.type}-${e.direction}`;
    if (seen.has(k)) return false;
    seen.add(k); return true;
  });
}

// Liquidity Sweeps (Purges): wick pierces previous swing then closes back
export function detectSweeps(bars, swings) {
  const sweeps = [];
  const refs = [
    ...swings.swingHighs.map(s => ({ ...s, kind: "buyside" })),
    ...swings.swingLows.map(s  => ({ ...s, kind: "sellside" })),
  ];
  for (const r of refs) {
    for (let j = r.index + 2; j < bars.length; j++) {
      const b = bars[j];
      if (r.kind === "buyside"  && b.high > r.price && b.close < r.price) {
        sweeps.push({ index: j, type: "buyside",  level: r.price, time: b.time, resistance: "low" });
        break;
      }
      if (r.kind === "sellside" && b.low  < r.price && b.close > r.price) {
        sweeps.push({ index: j, type: "sellside", level: r.price, time: b.time, resistance: "low" });
        break;
      }
    }
  }
  return sweeps;
}

// Premium / Discount of the current dealing range
export function premiumDiscount(bars) {
  if (!bars.length) return null;
  const high = Math.max(...bars.map(b => b.high));
  const low  = Math.min(...bars.map(b => b.low));
  const eq = (high + low) / 2;
  return {
    high, low, eq,
    premiumZone:  { top: high, bottom: eq },
    discountZone: { top: eq,   bottom: low },
    currentRegion: bars[bars.length - 1].close > eq ? "premium" : "discount",
  };
}

// Liquidity Sessions based on REAL UTC timestamps for futures markets.
// Asia 17:00–03:00 ET (21:00–07:00 UTC during DST), London 03:00–08:00 ET,
// NY 08:00–17:00 ET. We use UTC hour buckets as a practical approximation.
export function detectSessions(bars) {
  if (!bars.length) return [];
  const sessions = [];
  // Group by trading-day (UTC date) and bucket by session hour windows
  const dayMap = {};
  for (let i = 0; i < bars.length; i++) {
    const d = new Date(bars[i].time * 1000);
    const utcHour = d.getUTCHours();
    const dayKey = d.toISOString().slice(0, 10);
    let name = null;
    if (utcHour >= 21 || utcHour < 7)       name = "Asia";
    else if (utcHour >= 7 && utcHour < 12)  name = "London";
    else if (utcHour >= 12 && utcHour < 21) name = "New York";
    if (!name) continue;
    const key = `${dayKey}-${name}`;
    if (!dayMap[key]) {
      dayMap[key] = { name, from: i, to: i, high: bars[i].high, low: bars[i].low, dayKey };
    } else {
      dayMap[key].to = i;
      dayMap[key].high = Math.max(dayMap[key].high, bars[i].high);
      dayMap[key].low  = Math.min(dayMap[key].low,  bars[i].low);
    }
  }
  for (const k of Object.keys(dayMap)) {
    const s = dayMap[k];
    s.fromTime = bars[s.from].time;
    s.toTime   = bars[s.to].time;
    sessions.push(s);
  }
  return sessions.sort((a, b) => a.from - b.from);
}

// SMT Divergence — when main makes higher low/lower high but correlated does the opposite (or vice versa)
export function detectSMT(mainBars, smtBars, swings) {
  const events = [];
  // pair consecutive swing lows
  const sLows = swings.swingLows;
  for (let i = 1; i < sLows.length; i++) {
    const a = sLows[i - 1], b = sLows[i];
    if (a.index >= smtBars.length || b.index >= smtBars.length) continue;
    const mainHigherLow = b.price > a.price;
    const smtLowerLow   = smtBars[b.index].low < smtBars[a.index].low;
    if (mainHigherLow && smtLowerLow) {
      events.push({ index: b.index, type: "bullish_smt", level: b.price, time: b.time });
    }
    const mainLowerLow = b.price < a.price;
    const smtHigherLow = smtBars[b.index].low > smtBars[a.index].low;
    if (mainLowerLow && smtHigherLow) {
      events.push({ index: b.index, type: "bearish_smt", level: b.price, time: b.time });
    }
  }
  const sHighs = swings.swingHighs;
  for (let i = 1; i < sHighs.length; i++) {
    const a = sHighs[i - 1], b = sHighs[i];
    if (a.index >= smtBars.length || b.index >= smtBars.length) continue;
    if (b.price < a.price && smtBars[b.index].high > smtBars[a.index].high) {
      events.push({ index: b.index, type: "bearish_smt", level: b.price, time: b.time });
    }
    if (b.price > a.price && smtBars[b.index].high < smtBars[a.index].high) {
      events.push({ index: b.index, type: "bullish_smt", level: b.price, time: b.time });
    }
  }
  return events;
}

// Master analyzer — runs all detectors up to streamIndex
export function analyzeICT(mainBars, smtBars) {
  const swings = detectSwings(mainBars, 2);
  const fvgs = detectFVGs(mainBars);
  const ifvgs = detectIFVGs(fvgs);
  const structure = detectStructureShifts(mainBars, swings);
  const sweeps = detectSweeps(mainBars, swings);
  const pd = premiumDiscount(mainBars);
  const sessions = detectSessions(mainBars);
  const smt = detectSMT(mainBars, smtBars, swings);
  return { swings, fvgs, ifvgs, structure, sweeps, premiumDiscount: pd, sessions, smt };
}

// Given an analysis + an entry index, classify the entry context (used for AI prompt + post-trade card)
export function classifyEntry(entryIndex, entryPrice, side, analysis) {
  const ctx = {
    in_fvg: false,
    in_ifvg: false,
    near_sweep: false,
    after_mss: false,
    after_bos: false,
    region: analysis.premiumDiscount?.currentRegion || "unknown",
    session: null,
    smt_aligned: false,
  };
  // FVG / IFVG containment
  for (const f of analysis.fvgs) {
    if (entryIndex >= f.index && entryPrice >= f.bottom && entryPrice <= f.top) {
      ctx.in_fvg = true;
      ctx.fvg_type = f.type;
      if (f.mitigated && entryIndex >= f.mitigatedIndex) ctx.in_ifvg = true;
    }
  }
  // Sweep proximity (within 5 bars)
  const sweep = analysis.sweeps.find(s => Math.abs(s.index - entryIndex) <= 5);
  if (sweep) { ctx.near_sweep = true; ctx.sweep_type = sweep.type; }
  // MSS / BOS (within 8 bars before)
  const mss = analysis.structure.filter(s => s.type === "MSS" && entryIndex - s.index <= 8 && entryIndex - s.index >= 0).pop();
  if (mss) { ctx.after_mss = true; ctx.mss_direction = mss.direction; }
  const bos = analysis.structure.filter(s => s.type === "BOS" && entryIndex - s.index <= 8 && entryIndex - s.index >= 0).pop();
  if (bos) { ctx.after_bos = true; ctx.bos_direction = bos.direction; }
  // Session
  const sess = analysis.sessions.find(s => entryIndex >= s.from && entryIndex <= s.to);
  if (sess) ctx.session = sess.name;
  // SMT
  const smtEv = analysis.smt.find(s => Math.abs(s.index - entryIndex) <= 5);
  if (smtEv) {
    ctx.smt_aligned = (side === "BUY" && smtEv.type === "bullish_smt") || (side === "SELL" && smtEv.type === "bearish_smt");
    ctx.smt_event = smtEv.type;
  }
  return ctx;
}
